import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import { ChartInteractionUtils } from './ChartInteraction.parts'

export type ChartInteractionContextValue = {
  /** Dimension value under the tooltip pointer, if any. */
  highlightedX: string | null
  setHighlightedX: Dispatch<SetStateAction<string | null>>
  /** Sticky category selection (e.g. bar click / drill-through). */
  selectedX: string | null
  setSelectedX: Dispatch<SetStateAction<string | null>>
  /** Fired after a category select (bar click). Optional host wiring. */
  onCategorySelect?: (x: string) => void
}

const ChartInteractionContext =
  createContext<ChartInteractionContextValue | null>(null)

export const useChartInteraction = () => {
  const context = useContext(ChartInteractionContext)
  if (context == null) {
    throw new Error(
      'useChartInteraction must be used within ChartInteraction.Provider',
    )
  }
  return context
}

export interface ChartInteractionProviderProps {
  children: ReactNode
  /** Optional initial hover dimension for Storybook / tests. */
  initialHighlightedX?: string | null
  /**
   * Uncontrolled sticky selection seed. Prefer controlled `selectedX` when
   * selection must survive a remount.
   */
  initialSelectedX?: string | null
  selectedX?: string | null
  onSelectedXChange?: (x: string | null) => void
  onCategorySelect?: (x: string) => void
}

const Provider = ({
  children,
  initialHighlightedX = null,
  initialSelectedX = null,
  selectedX: selectedXProp,
  onSelectedXChange,
  onCategorySelect,
}: ChartInteractionProviderProps) => {
  const [highlightedX, setHighlightedX] = useState<string | null>(
    initialHighlightedX,
  )
  const [uncontrolledSelectedX, setUncontrolledSelectedX] = useState<
    string | null
  >(initialSelectedX)
  const isSelectedControlled = selectedXProp !== undefined
  const selectedX =
    (isSelectedControlled ? selectedXProp : uncontrolledSelectedX) ?? null

  const setSelectedX: Dispatch<SetStateAction<string | null>> = useCallback(
    update => {
      const previous = isSelectedControlled
        ? (selectedXProp ?? null)
        : uncontrolledSelectedX
      const next = typeof update === 'function' ? update(previous) : update
      if (!isSelectedControlled) {
        setUncontrolledSelectedX(next)
      }
      onSelectedXChange?.(next)
    },
    [
      isSelectedControlled,
      onSelectedXChange,
      selectedXProp,
      uncontrolledSelectedX,
    ],
  )

  const value = useMemo(
    () => ({
      highlightedX,
      setHighlightedX,
      selectedX,
      setSelectedX,
      onCategorySelect,
    }),
    [highlightedX, selectedX, setSelectedX, onCategorySelect],
  )

  return (
    <ChartInteractionContext.Provider value={value}>
      {children}
    </ChartInteractionContext.Provider>
  )
}

export const ChartInteraction = {
  Provider,
  useChartInteraction,
  Utils: ChartInteractionUtils,
}
